// Workspaces on a schedule (docs/features/workspace-schedule.md): what Home's Mac section says when the Mac's
// workspace is off by its schedule ("Off until 8:00 (schedule)" with Start Now), waiting for the agent before a
// scheduled stop, or started by hand past its hours. The Mac keeps the workspace's WebRTC room open while it is
// off, so this browser still connects and hears it over the wire channel (wire.js, kind 'schedule').
//
// One seam: `scheduleOf(mac)` → the Mac's word on this room's workspace, or null; `scheduleCard(entry, {allowed,
// asked, refused})` → {symbol, title, detail, action: {act, label} | null} or null.
import { registerWire } from './wire.js'

// what the Mac says, kept on its link; the app repaints Home from `onSchedule`
registerWire('schedule', (link, payload, m) => { link.schedule = m; link.onSchedule?.(link.schedule) })

export function scheduleOf(mac) {
  const list = mac?.windows?.schedule?.workspaces
  if (!list?.length) return null
  return list.find((w) => w.room && w.room === mac.room) || (list.length === 1 ? list[0] : null)
}

/// Seconds since 1970 in this browser's clock: "8:00 AM", "8:00 AM tomorrow", "Mon 8:00 AM".
function moment(seconds) {
  const d = new Date(seconds * 1000), now = new Date()
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((day(d) - day(now)) / 86400000)
  if (days === 0) return time
  if (days === 1) return time + ' tomorrow'
  return d.toLocaleDateString([], { weekday: 'short' }) + ' ' + time
}

export function scheduleCard(entry, { allowed = false, asked = false, refused = '' } = {}) {
  if (!entry) return null
  if (entry.offUntil != null) {
    const starting = asked || entry.state === 'starting'
    return {
      symbol: 'lucide:moon', title: `Off until ${entry.offUntil ? moment(entry.offUntil) : 'you start it'} (schedule)`,
      detail: refused || (allowed ? entry.schedule || '' : '“Allow starting from a paired device” is off on the Mac.'),
      action: allowed ? { act: starting ? '' : 'start', label: starting ? 'Starting…' : 'Start Now' } : null,
    }
  }
  // stopped by hand, or opencode quit (docs/features/mac-room.md): the Mac app still holds the room, so this browser
  // is here to say so — the card with the button that fixes it
  if (entry.state === 'stopped' || entry.state === 'failed' || entry.state === 'starting') {
    const starting = asked || entry.state === 'starting'
    return {
      symbol: 'lucide:circle-pause', title: entry.state === 'starting' ? `“${entry.name}” is starting` : `“${entry.name}” is stopped`,
      detail: refused || (allowed ? 'Its sessions come back once it runs.' : '“Allow starting from a paired device” is off on the Mac.'),
      action: allowed ? { act: starting ? '' : 'start', label: starting ? 'Starting…' : entry.state === 'failed' ? 'Restart' : 'Start' } : null,
    }
  }
  if (entry.stopsBy) return { symbol: 'lucide:clock', title: `Stopping for the schedule once the agent finishes (by ${moment(entry.stopsBy)})`, detail: entry.schedule || '', action: null }
  if (entry.overrideUntil) return { symbol: 'lucide:clock', title: `Started by hand — runs until ${moment(entry.overrideUntil)}, then the schedule applies`, detail: entry.schedule || '', action: null }
  return null
}
